import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import Tag from "antd/es/tag";
import cn from "classnames";
import { useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { InlineSelectField, SelectField, SelectFieldProps } from "@mapmycustomers/ui";

import EntityBadge from "@app/component/EntityBadge";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { ensureStagesFetched } from "@app/store/deal/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "./StageSelectBox.module.scss";

const showAll = () => true;

interface Props extends Pick<SelectFieldProps, "disabled" | "placeholder" | "prefixIcon" | "size"> {
  allowClear?: boolean;
  className?: string;
  ensureStagesFetched: typeof ensureStagesFetched.request;
  filterStages?: (stage: Stage) => boolean;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  inline?: boolean;
  label?: ReactNode;
  onChange?: (stagesIds: Stage["id"][]) => void;
  selectedFunnelsIds?: Funnel["id"][];
  showArrow?: boolean;
  showSearch?: boolean;
  value?: Stage["id"][];
}

const StageSelectBox = ({
  allowClear,
  className,
  ensureStagesFetched,
  filterStages = showAll,
  funnels,
  funnelStages,
  inline,
  label,
  onChange,
  placeholder,
  selectedFunnelsIds,
  showArrow,
  showSearch,
  size = "middle",
  value,
  ...props
}: Props) => {
  const intl = useIntl();

  useEffect(() => {
    ensureStagesFetched(undefined);
  }, [ensureStagesFetched]);

  const allFunnelIds = useMemo(() => funnels.map(({ id }) => id), [funnels]);

  // only allow selecting stages for these funnels
  // when selectedFunnelIds is given and is not empty, use it, otherwise allow stages for all funnels
  const funnelIds = new Set(selectedFunnelsIds?.length ? selectedFunnelsIds : allFunnelIds);

  // only show stages which it is possible to select. Solving a bug for this scenario:
  // select stage X from funnel A
  // select funnel B
  // now we should hide stage X from options (even though technically it is still applied)
  const selectableStagesIds = new Set(
    Array.from(funnelIds)
      .flatMap((funnelId) => funnelStages[funnelId])
      .filter(isDefined)
      .map(({ id }) => id)
  );

  const handleChange = useCallback((stagesIds: Stage["id"][]) => onChange?.(stagesIds), [onChange]);

  const SelectComponent = inline ? InlineSelectField<Stage["id"][]> : SelectField<Stage["id"][]>;

  const stagesMap = useMemo(
    () =>
      new Map(
        Object.values(funnelStages)
          .flat()
          .map((stage) => [stage.id, stage])
      ),
    [funnelStages]
  );

  const valueRender = useCallback(
    (stageIds: null | Stage["id"][] | undefined) => {
      if (!stageIds?.length) {
        return "";
      }
      return (
        <div className={styles.inlineValue}>
          {stageIds
            .map((stageId) => {
              const stage = stagesMap.get(stageId);
              return stage ? (
                <Tag key={stage.id}>
                  <EntityBadge entity={stage} />
                </Tag>
              ) : null;
            })
            .filter(isDefined)}
        </div>
      );
    },
    [stagesMap]
  );

  return (
    <SelectComponent
      allowClear={allowClear}
      className={cn(styles.container, className)}
      filterOption={defaultFilterOption}
      label={label}
      maxTagCount="responsive"
      mode="multiple"
      onChange={handleChange}
      placeholder={
        placeholder ??
        intl.formatMessage({
          id: "stageField.placeholder",
          defaultMessage: "Select Stage",
          description: "Select Stage field's placeholder",
        })
      }
      showArrow={showArrow}
      showSearch={showSearch}
      size={size}
      value={(value ?? []).filter((id) => selectableStagesIds.has(id))}
      {...(inline ? { showFooterButtons: false, valueRender } : {})}
      {...props}
    >
      {funnels
        .filter(({ id }) => funnelIds.has(id))
        .map((funnel) => (
          <Select.OptGroup key={funnel.id} label={funnel.name}>
            {(funnelStages[funnel.id] ?? []).filter(filterStages).map((stage) => (
              <Select.Option key={stage.id} label={stage.name} value={stage.id}>
                <EntityBadge entity={stage} />
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
    </SelectComponent>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
});

const mapDispatchToProps = {
  ensureStagesFetched: ensureStagesFetched.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(StageSelectBox);
